import classNames from 'classnames';
import { useId } from 'react';
import type { ReactNode } from 'react';

import { useUiMessages } from '../../messages';
import { Button } from '../Button';
import { CloseButton } from '../CloseButton';
import { ProgressBar } from '../ProgressBar';
import { Typography } from '../Typography';

import styles from './Notification.module.css';
import { NotificationMedia } from './NotificationMedia';
import type { NotificationMediaSpec } from './NotificationMedia';

export type {
  NotificationMediaSpec,
  NotificationMediaVariant,
  NotificationIconShape,
} from './NotificationMedia';

export type NotificationRole = 'status' | 'alert';
export type NotificationActionHierarchy = 'link-gray' | 'link-color';

export interface NotificationAction {
  label: string;
  /** Reported to `onAction` / resolved by the manager. Defaults to `label`. */
  value?: string;
  hierarchy?: NotificationActionHierarchy;
  disabled?: boolean;
  onClick?: () => void;
  /** Dismiss the toast after this action runs (default true). Set false to keep it open. */
  dismiss?: boolean;
}

export interface NotificationLabels {
  close: string;
}

const DEFAULT_LABELS: NotificationLabels = { close: 'Close' };

export interface NotificationProps {
  title: ReactNode;
  description?: ReactNode;
  /** Leading visual: ripple/square featured icon, image, avatar, or none. */
  media?: NotificationMediaSpec;
  /** Small meta beside the title, e.g. a relative timestamp. */
  timestamp?: ReactNode;
  /** Upload-style progress row (0–100) shown under the text. */
  progress?: number;
  /** Label beside the progress bar (defaults to a rounded percentage). */
  progressLabel?: ReactNode;
  actions?: NotificationAction[];
  showClose?: boolean;
  onClose?: () => void;
  onAction?: (action: NotificationAction) => void;
  /** `status` (polite, default) or `alert` (assertive) live-region semantics. */
  role?: NotificationRole;
  labels?: Partial<NotificationLabels>;
  className?: string;
  id?: string;
}

/**
 * Presentational notification (toast) surface: a leading visual (rippling/square featured icon,
 * image, or avatar), a title with optional timestamp + supporting text, an optional progress row,
 * and text-link actions, with a close button. Announced via `role="status"`/`"alert"` and wired
 * `aria-labelledby`/`aria-describedby`. Purely presentational — timing, stacking and placement
 * belong to `NotificationProvider`.
 *
 * @interim Seeded from the Figma "Notification toast"; replaceable by the UI Kit without API breakage.
 */
export function Notification({
  title,
  description,
  media = { kind: 'none' },
  timestamp,
  progress,
  progressLabel,
  actions = [],
  showClose = true,
  onClose,
  onAction,
  role = 'status',
  labels,
  className,
  id,
}: NotificationProps) {
  const messages = useUiMessages();
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const titleId = `${baseId}-title`;
  const descId = `${baseId}-desc`;
  const t = { ...DEFAULT_LABELS, ...messages.notification, ...labels };

  const isImage = media.kind === 'image';
  const hasProgress = progress != null;

  const handleAction = (action: NotificationAction) => {
    action.onClick?.();
    onAction?.(action);
  };

  return (
    <div
      role={role}
      aria-labelledby={titleId}
      aria-describedby={description != null ? descId : undefined}
      className={classNames(styles.card, isImage && styles.cardImage, className)}
    >
      {isImage ? <img className={styles.image} src={media.src} alt={media.alt ?? ''} /> : null}

      <div className={styles.inner}>
        {!isImage && media.kind !== 'none' ? (
          <NotificationMedia spec={media} className={classNames(styles.leading)} />
        ) : null}

        <div className={styles.content}>
          <div className={classNames(styles.text, showClose && styles.textInset)}>
            <div className={styles.titleRow}>
              <Typography
                as="p"
                id={titleId}
                variant="text-sm"
                weight="semibold"
                className={classNames(styles.title)}
              >
                {title}
              </Typography>
              {timestamp != null ? (
                <Typography as="span" variant="text-sm" className={classNames(styles.timestamp)}>
                  {timestamp}
                </Typography>
              ) : null}
            </div>
            {description != null ? (
              <Typography
                as="p"
                id={descId}
                variant="text-sm"
                className={classNames(styles.description)}
              >
                {description}
              </Typography>
            ) : null}
          </div>

          {hasProgress ? (
            <div className={styles.progress}>
              <ProgressBar value={progress} ariaLabelledby={titleId} />
              <Typography
                as="span"
                variant="text-sm"
                weight="medium"
                className={classNames(styles.progressLabel)}
              >
                {progressLabel ?? `${String(Math.round(progress))}%`}
              </Typography>
            </div>
          ) : null}

          {actions.length > 0 ? (
            <div className={styles.actions}>
              {actions.map((action) => (
                <Button
                  key={action.value ?? action.label}
                  type="button"
                  size="sm"
                  hierarchy={action.hierarchy ?? 'link-gray'}
                  disabled={action.disabled ?? false}
                  onClick={() => {
                    handleAction(action);
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {showClose ? (
        <CloseButton size="md" label={t.close} className={styles.close} onClick={onClose} />
      ) : null}
    </div>
  );
}
